/* Managing the Categories money is filed under — STORY-021.
 *
 * ── two lists, because a Category belongs to one Direction ──────────────────
 * Not one list with a marker. The Direction is what stops an income category
 * ending up on an expense, and the source workbook shows why that matters: it
 * had `Satış` on both sides, so its income total silently included expenses.
 * Two headed lists make the split perceivable rather than implied by which
 * options happen to appear in a picker elsewhere.
 *
 * ── a duplicate names what it collided with ─────────────────────────────────
 * Names are compared folded, so "Baskı" collides with "Baski" and "IŞIK" with
 * "ışık". Told only "that already exists", somebody looking at two spellings
 * that differ on screen would reasonably think it a bug.
 */

import {
  CATEGORY_NAME_MAX,
  type Category,
  TRANSACTION_DIRECTIONS,
  type TransactionDirection,
} from '@eventuley/contracts';
import type { MessageKey } from '@eventuley/i18n';
import { useId, useState } from 'react';
import { ApiError, NetworkError } from '../../../shared/api/client.js';
import { useTranslate } from '../../../shared/i18n/index.js';
import { Button, Callout, TextField } from '../../../shared/ui/index.js';
import {
  useCategories,
  useCreateCategory,
  useDeleteCategory,
  useRenameCategory,
} from '../api/index.js';

const HEADINGS = {
  income: 'categories.income_heading',
  expense: 'categories.expense_heading',
} as const satisfies Record<TransactionDirection, MessageKey>;

const DIRECTION_LABELS = {
  income: 'money.direction.income',
  expense: 'money.direction.expense',
} as const satisfies Record<TransactionDirection, MessageKey>;

/** The rule the server gave for one field, or undefined. */
function fieldRule(error: unknown, path: string): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  return error.fields?.find((field) => field.path === path)?.rule;
}

/**
 * A message key for whatever went wrong, or undefined if nothing did.
 *
 * Shared by the add form and the rename form because they were each getting
 * this wrong in a different way — one told somebody their non-empty name was
 * blank when the network dropped, the other rendered nothing at all for
 * anything that was not a duplicate. Both are the same failure the ledger form
 * had and this story records fixing: a refusal nobody can see is
 * indistinguishable from a screen that has stopped working.
 */
function refusalKey(error: unknown): MessageKey | undefined {
  if (!error) return undefined;
  if (error instanceof NetworkError) return 'error.network';
  if (fieldRule(error, 'name') === 'duplicate') return undefined; // named separately
  if (fieldRule(error, 'name') !== undefined) return 'categories.name_required';
  /* Anything else — a 500, a 404, a rule we have no wording for. Vague on
   * purpose rather than guessing at a cause. */
  return 'error.unexpected';
}

export function CategoriesPanel({ organisationId }: { readonly organisationId: string }) {
  const t = useTranslate();
  const directionId = useId();

  const { data, error, isLoading } = useCategories(organisationId);
  const create = useCreateCategory(organisationId);

  const [name, setName] = useState('');
  const [direction, setDirection] = useState<TransactionDirection>('expense');

  if (error) {
    return (
      <Callout tone="danger">
        {error instanceof NetworkError ? t('error.network') : t('error.unexpected')}
      </Callout>
    );
  }

  const categories = data?.categories ?? [];

  const duplicate = fieldRule(create.error, 'name') === 'duplicate';
  /* The server sends the existing name as the rule, so the message can name it
   * rather than leaving somebody to guess which row it meant. */
  const collidedWith = fieldRule(create.error, 'existing');
  const createRefusal = refusalKey(create.error);

  return (
    <>
      <p className="field__hint">{t('categories.explainer')}</p>

      <section className="settings__section">
        <h2>{t('categories.add_heading')}</h2>
        <form
          onSubmit={(submitted) => {
            submitted.preventDefault();
            create.mutate({ name, direction }, { onSuccess: () => setName('') });
          }}
          noValidate
        >
          <TextField
            label={t('categories.name_label')}
            value={name}
            maxLength={CATEGORY_NAME_MAX}
            onChange={(changed) => setName(changed.target.value)}
            error={
              duplicate
                ? collidedWith
                  ? t('categories.duplicate_named', { name: collidedWith })
                  : t('categories.duplicate')
                : createRefusal
                  ? t(createRefusal)
                  : undefined
            }
          />

          {/* A real select rather than styled divs — the story asks for the
              Direction to be perceivable rather than implied, and this is the
              control that is keyboard-operable and screen-reader correct for
              free. */}
          <div className="field">
            <label className="field__label" htmlFor={directionId}>
              {t('money.direction_label')}
            </label>
            <select
              id={directionId}
              className="field__control"
              value={direction}
              onChange={(changed) => setDirection(changed.target.value as TransactionDirection)}
            >
              {TRANSACTION_DIRECTIONS.map((value) => (
                <option key={value} value={value}>
                  {t(DIRECTION_LABELS[value])}
                </option>
              ))}
            </select>
          </div>

          <Button type="submit" busy={create.isPending}>
            {t('categories.add_submit')}
          </Button>
        </form>
      </section>

      {isLoading ? <p className="field__hint">{t('common.loading')}</p> : null}

      {TRANSACTION_DIRECTIONS.map((value) => {
        const forDirection = categories.filter((category) => category.direction === value);
        return (
          <section className="settings__section" key={value}>
            <h2>{t(HEADINGS[value])}</h2>
            {forDirection.length === 0 ? (
              <p className="field__hint">{t('categories.none')}</p>
            ) : (
              <ul className="row-list row-list--tight">
                {forDirection.map((category) => (
                  <CategoryRow
                    key={category.id}
                    category={category}
                    organisationId={organisationId}
                  />
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </>
  );
}

function CategoryRow({
  category,
  organisationId,
}: {
  readonly category: Category;
  readonly organisationId: string;
}) {
  const t = useTranslate();
  const rename = useRenameCategory(organisationId);
  const remove = useDeleteCategory(organisationId);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(category.name);

  /* Resynced when the row changes underneath — the same lesson as the Event
   * status control and the guest Pax field, where a refetch left a stale value
   * armed to be saved. */
  const [seen, setSeen] = useState(category.name);
  if (seen !== category.name) {
    setSeen(category.name);
    setDraft(category.name);
  }

  const inUse =
    remove.error instanceof ApiError && remove.error.code === 'conflict'
      ? Number(fieldRule(remove.error, 'transactionCount') ?? 0)
      : null;

  const duplicate = fieldRule(rename.error, 'name') === 'duplicate';
  const collidedWith = fieldRule(rename.error, 'existing');
  const renameRefusal = refusalKey(rename.error);

  return (
    <li className="row">
      <span className="row__main">
        <span className="row__title">{category.name}</span>
      </span>

      <span className="row__actions">
        <Button
          variant="ghost"
          onClick={() => {
            setDraft(category.name);
            rename.reset();
            setEditing(!editing);
          }}
        >
          {editing ? t('categories.cancel') : t('categories.rename')}
        </Button>
        <Button variant="ghost" busy={remove.isPending} onClick={() => remove.mutate(category.id)}>
          {t('categories.delete')}
        </Button>
      </span>

      {/* Named with the count, because "used by 34 transactions" is a decision
          somebody can make and "in use" is not. Deleting anyway is not
          offered — the money would go with it. */}
      {inUse !== null ? (
        <div className="row__expand">
          <Callout tone="warning">{t('categories.in_use', { count: inUse })}</Callout>
        </div>
      ) : remove.error ? (
        <div className="row__expand">
          {/* Every other way a delete can fail. Without this the button simply
              un-busied and the row stayed put, which reads as a screen that has
              stopped working rather than as a refusal. */}
          <Callout tone="danger">
            {remove.error instanceof NetworkError
              ? t('error.network')
              : t('categories.delete_failed')}
          </Callout>
        </div>
      ) : null}

      {editing ? (
        <form
          className="row__expand"
          onSubmit={(submitted) => {
            submitted.preventDefault();
            rename.mutate(
              { categoryId: category.id, name: draft },
              { onSuccess: () => setEditing(false) },
            );
          }}
          noValidate
        >
          <TextField
            label={t('categories.rename_label', { name: category.name })}
            value={draft}
            maxLength={CATEGORY_NAME_MAX}
            onChange={(changed) => setDraft(changed.target.value)}
            error={
              duplicate
                ? collidedWith
                  ? t('categories.duplicate_named', { name: collidedWith })
                  : t('categories.duplicate')
                : renameRefusal
                  ? t(renameRefusal)
                  : undefined
            }
          />
          {/* Said where it is decided: renaming reaches every Transaction
              already filed under it, which is the point of a Category being a
              record rather than free text. */}
          <p className="field__hint">{t('categories.rename_hint')}</p>
          <div className="btn-row">
            <Button type="submit" busy={rename.isPending}>
              {t('categories.save')}
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>
              {t('categories.cancel')}
            </Button>
          </div>
        </form>
      ) : null}
    </li>
  );
}
